import React, { useEffect, useState } from 'react';
import { Save, Loader2, CheckCircle2, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { api } from '../../lib/api';
import ImageInput from '../../components/admin/ImageInput';
import { Field, TextInput, TextArea, NumberInput, Button } from '../../components/admin/FormControls';

const SectionCard = ({ title, description, children }) => (
  <div className="rounded-lg border border-[#E6DDC8] bg-white p-6 shadow-sm">
    <div className="mb-5 border-b border-[#E6DDC8] pb-3">
      <h3 className="font-serif text-lg font-bold text-[#0B2545]">{title}</h3>
      {description && <p className="mt-1 text-[12.5px] text-[#0B2545]/65">{description}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const ParagraphList = ({ label, paragraphs, onChange, hint }) => {
  const list = paragraphs || [];
  const update = (i, v) => onChange(list.map((p, idx) => (idx === i ? v : p)));
  const add = () => onChange([...list, '']);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-[12.5px] font-medium text-[#0B2545]">{label}</label>
        <Button variant="outline" onClick={add}><Plus size={12} /> Add</Button>
      </div>
      {hint && <div className="mb-2 text-[11.5px] text-[#0B2545]/55">{hint}</div>}
      <div className="space-y-2">
        {list.map((p, i) => (
          <div key={i} className="flex items-start gap-2">
            <TextArea rows={3} value={p} onChange={(v) => update(i, v)} />
            <button
              type="button"
              onClick={() => remove(i)}
              className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        {list.length === 0 && (
          <div className="rounded-md border border-dashed border-[#E6DDC8] p-3 text-center text-[12.5px] text-[#0B2545]/55">
            No items. Click "Add" to start.
          </div>
        )}
      </div>
    </div>
  );
};

const StringList = ({ label, items, onChange }) => {
  const list = items || [];
  const update = (i, v) => onChange(list.map((p, idx) => (idx === i ? v : p)));
  const add = () => onChange([...list, '']);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-[12.5px] font-medium text-[#0B2545]">{label}</label>
        <Button variant="outline" onClick={add}><Plus size={12} /> Add</Button>
      </div>
      <div className="space-y-2">
        {list.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <TextInput value={p} onChange={(v) => update(i, v)} />
            <button type="button" onClick={() => remove(i)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PageHeaderEditor = ({ label, value, onChange }) => (
  <div className="rounded-md border border-[#E6DDC8] bg-[#FAF6EC] p-4">
    <div className="mb-3 text-[12.5px] font-semibold text-[#0B2545]">{label}</div>
    <div className="space-y-3">
      <Field label="Title"><TextInput value={value?.title || ''} onChange={(v) => onChange({ ...value, title: v })} /></Field>
      <Field label="Subtitle"><TextArea rows={2} value={value?.subtitle || ''} onChange={(v) => onChange({ ...value, subtitle: v })} /></Field>
    </div>
  </div>
);

const LegalLinksEditor = ({ links, onChange }) => {
  const list = links || [];
  const update = (i, patch) => onChange(list.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const add = () => onChange([...list, { label: 'New link', href: '#' }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-[12.5px] font-medium text-[#0B2545]">Legal links (footer bottom)</label>
        <Button variant="outline" onClick={add}><Plus size={12} /> Add link</Button>
      </div>
      <div className="space-y-2">
        {list.map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <TextInput value={l.label} onChange={(v) => update(i, { label: v })} placeholder="Label" />
            <TextInput value={l.href} onChange={(v) => update(i, { href: v })} placeholder="URL or #" />
            <button onClick={() => remove(i)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const GovernmentLinksEditor = ({ links, onChange }) => {
  const list = (links || []).slice().sort((a, b) => a.order - b.order);
  const update = (i, patch) => onChange(list.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const add = () => onChange([...list, { slug: 'new-institution', name: 'New Institution', body_paragraphs: [], order: (list[list.length - 1]?.order || 0) + 1 }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next.map((l, idx) => ({ ...l, order: idx + 1 })));
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-[#0B2545]/65">
          These items appear in the header "Government" menu and footer. Each has its own page at /government/&lt;slug&gt;.
        </p>
        <Button variant="outline" onClick={add}><Plus size={12} /> Add institution</Button>
      </div>
      {list.map((g, i) => (
        <div key={i} className="rounded-md border border-[#E6DDC8] bg-[#FAF6EC] p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="inline-flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0B2545] font-serif text-xs font-bold text-white">{g.order}</span>
              <span className="text-[12.5px] text-[#0B2545]/65">Order</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={12} /></Button>
              <Button variant="outline" onClick={() => move(i, +1)} disabled={i === list.length - 1}><ArrowDown size={12} /></Button>
              <button onClick={() => remove(i)} className="text-[12.5px] text-red-600 hover:underline"><Trash2 size={12} className="inline" /> Remove</button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Name"><TextInput value={g.name} onChange={(v) => update(i, { name: v })} /></Field>
            <Field label="Slug" hint="URL: /government/<slug>"><TextInput value={g.slug} onChange={(v) => update(i, { slug: v })} /></Field>
          </div>
          <div className="mt-3">
            <ParagraphList
              label="Body paragraphs (shown on this institution's page)"
              paragraphs={g.body_paragraphs}
              onChange={(v) => update(i, { body_paragraphs: v })}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    const { data } = await api.get('/settings');
    setS(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...rest } = s;
      await api.put('/settings', rest);
      flash('All settings saved');
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (path, value) => {
    setS((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...(ref[keys[i]] || {}) };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  if (loading || !s) {
    return (
      <div className="text-[#0B2545]/60">
        <Loader2 className="inline animate-spin" size={14} /> Loading...
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#0B2545]">Site Settings</h1>
          <p className="mt-1 text-[13.5px] text-[#0B2545]/65">
            Edit every text, label, image and link on the public website.
          </p>
        </div>
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save all settings
        </Button>
      </div>

      {msg && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-700">
          <CheckCircle2 size={14} /> {msg}
        </div>
      )}

      <div className="space-y-6">
        <SectionCard title="Branding" description="Logo and site name shown in the header and footer.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ImageInput label="Logo / Seal" value={s.logo_url} onChange={(v) => set('logo_url', v)} height={140} />
            <div className="space-y-4">
              <Field label="Site name"><TextInput value={s.site_name} onChange={(v) => set('site_name', v)} /></Field>
              <Field label="Site subtitle"><TextInput value={s.site_subtitle} onChange={(v) => set('site_subtitle', v)} /></Field>
              <Field label="Footer about text"><TextArea rows={3} value={s.footer_about} onChange={(v) => set('footer_about', v)} /></Field>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Hero buttons" description="Buttons over the home page slider.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="Primary label"><TextInput value={s.hero_buttons.primary_label} onChange={(v) => set('hero_buttons.primary_label', v)} /></Field>
            <Field label="Secondary label"><TextInput value={s.hero_buttons.secondary_label} onChange={(v) => set('hero_buttons.secondary_label', v)} /></Field>
            <Field label="Tertiary label"><TextInput value={s.hero_buttons.tertiary_label} onChange={(v) => set('hero_buttons.tertiary_label', v)} /></Field>
            <Field label="Primary link"><TextInput value={s.hero_buttons.primary_link} onChange={(v) => set('hero_buttons.primary_link', v)} /></Field>
            <Field label="Secondary link"><TextInput value={s.hero_buttons.secondary_link} onChange={(v) => set('hero_buttons.secondary_link', v)} /></Field>
            <Field label="Tertiary link"><TextInput value={s.hero_buttons.tertiary_link} onChange={(v) => set('hero_buttons.tertiary_link', v)} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="President" description="Photo, name and message shown on the home page and Office of the President page.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ImageInput label="Photo" value={s.president.photo_url} onChange={(v) => set('president.photo_url', v)} height={260} />
            <div className="space-y-4">
              <Field label="Name"><TextInput value={s.president.name} onChange={(v) => set('president.name', v)} /></Field>
              <Field label="Title"><TextInput value={s.president.title} onChange={(v) => set('president.title', v)} /></Field>
              <Field label="Headline"><TextInput value={s.president.headline} onChange={(v) => set('president.headline', v)} /></Field>
            </div>
          </div>
          <ParagraphList label="Message paragraphs" paragraphs={s.president.paragraphs} onChange={(v) => set('president.paragraphs', v)} />
        </SectionCard>

        <SectionCard title="State Profile" description="Map, statistics and description shown in the State Profile section.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ImageInput label="Map image" value={s.state_profile.map_image_url} onChange={(v) => set('state_profile.map_image_url', v)} height={220} />
            <div className="space-y-3">
              <Field label="Population"><TextInput value={s.state_profile.population} onChange={(v) => set('state_profile.population', v)} /></Field>
              <Field label="Regions"><TextInput value={s.state_profile.regions} onChange={(v) => set('state_profile.regions', v)} /></Field>
              <Field label="Coastline"><TextInput value={s.state_profile.coastline} onChange={(v) => set('state_profile.coastline', v)} /></Field>
            </div>
          </div>
          <Field label="Description"><TextArea rows={6} value={s.state_profile.description} onChange={(v) => set('state_profile.description', v)} /></Field>
        </SectionCard>

        <SectionCard title="Homepage section titles" description="Eyebrows, titles and button labels for each homepage section.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="President eyebrow"><TextInput value={s.home_sections?.president_eyebrow || ''} onChange={(v) => set('home_sections.president_eyebrow', v)} /></Field>
            <Field label="President button label"><TextInput value={s.home_sections?.president_button_label || ''} onChange={(v) => set('home_sections.president_button_label', v)} /></Field>
            <Field label="State profile eyebrow"><TextInput value={s.home_sections?.state_profile_eyebrow || ''} onChange={(v) => set('home_sections.state_profile_eyebrow', v)} /></Field>
            <Field label="State profile title"><TextInput value={s.home_sections?.state_profile_title || ''} onChange={(v) => set('home_sections.state_profile_title', v)} /></Field>
            <Field label="Ministries eyebrow"><TextInput value={s.home_sections?.ministries_eyebrow || ''} onChange={(v) => set('home_sections.ministries_eyebrow', v)} /></Field>
            <Field label="Ministries title"><TextInput value={s.home_sections?.ministries_title || ''} onChange={(v) => set('home_sections.ministries_title', v)} /></Field>
            <Field label="Ministries description"><TextArea rows={3} value={s.home_sections?.ministries_description || ''} onChange={(v) => set('home_sections.ministries_description', v)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="News eyebrow"><TextInput value={s.home_sections?.news_eyebrow || ''} onChange={(v) => set('home_sections.news_eyebrow', v)} /></Field>
              <Field label="News title"><TextInput value={s.home_sections?.news_title || ''} onChange={(v) => set('home_sections.news_title', v)} /></Field>
            </div>
            <Field label="News button label"><TextInput value={s.home_sections?.news_button_label || ''} onChange={(v) => set('home_sections.news_button_label', v)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Events eyebrow"><TextInput value={s.home_sections?.events_eyebrow || ''} onChange={(v) => set('home_sections.events_eyebrow', v)} /></Field>
              <Field label="Events title"><TextInput value={s.home_sections?.events_title || ''} onChange={(v) => set('home_sections.events_title', v)} /></Field>
            </div>
            <Field label="Events button label"><TextInput value={s.home_sections?.events_button_label || ''} onChange={(v) => set('home_sections.events_button_label', v)} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Page headers" description="Title and subtitle shown at the top of each internal page.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {['news', 'events', 'ministries', 'investment', 'documents', 'about', 'contact'].map((key) => (
              <PageHeaderEditor
                key={key}
                label={key[0].toUpperCase() + key.slice(1) + ' page'}
                value={s.page_headers?.[key] || { title: '', subtitle: '' }}
                onChange={(v) => set(`page_headers.${key}`, v)}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Government links" description="Items in the header Government dropdown and footer. Each has its own page.">
          <GovernmentLinksEditor
            links={s.government_links || []}
            onChange={(v) => set('government_links', v)}
          />
        </SectionCard>

        <SectionCard title="Ministry detail template" description="Text shown on every ministry's detail page. Use {name} to insert the ministry name.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Eyebrow"><TextInput value={s.ministry_template?.eyebrow || ''} onChange={(v) => set('ministry_template.eyebrow', v)} /></Field>
            <Field label="Title"><TextInput value={s.ministry_template?.title || ''} onChange={(v) => set('ministry_template.title', v)} /></Field>
          </div>
          <ParagraphList
            label="Paragraphs (use {name} as placeholder)"
            paragraphs={s.ministry_template?.paragraphs}
            onChange={(v) => set('ministry_template.paragraphs', v)}
          />
          <StringList
            label="Pillars (small cards shown below paragraphs)"
            items={s.ministry_template?.pillars}
            onChange={(v) => set('ministry_template.pillars', v)}
          />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Contact card title"><TextInput value={s.ministry_template?.contact_card_title || ''} onChange={(v) => set('ministry_template.contact_card_title', v)} /></Field>
            <Field label="Contact card description"><TextInput value={s.ministry_template?.contact_card_description || ''} onChange={(v) => set('ministry_template.contact_card_description', v)} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Investment sector template" description="Text shown on every sector detail page. Placeholders: {name}, {name_lower}.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Eyebrow"><TextInput value={s.investment_sector_template?.eyebrow || ''} onChange={(v) => set('investment_sector_template.eyebrow', v)} /></Field>
            <Field label="Title template"><TextInput value={s.investment_sector_template?.title_template || ''} onChange={(v) => set('investment_sector_template.title_template', v)} hint="Use {name}" /></Field>
          </div>
          <ParagraphList
            label="Paragraphs"
            paragraphs={s.investment_sector_template?.paragraphs}
            onChange={(v) => set('investment_sector_template.paragraphs', v)}
          />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Contact card title"><TextInput value={s.investment_sector_template?.contact_card_title || ''} onChange={(v) => set('investment_sector_template.contact_card_title', v)} /></Field>
            <Field label="Contact card description"><TextInput value={s.investment_sector_template?.contact_card_description || ''} onChange={(v) => set('investment_sector_template.contact_card_description', v)} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Investment Intro (home section)">
          <Field label="Eyebrow"><TextInput value={s.investment_intro.eyebrow} onChange={(v) => set('investment_intro.eyebrow', v)} /></Field>
          <Field label="Headline"><TextInput value={s.investment_intro.headline} onChange={(v) => set('investment_intro.headline', v)} /></Field>
          <Field label="Description"><TextArea rows={3} value={s.investment_intro.description} onChange={(v) => set('investment_intro.description', v)} /></Field>
        </SectionCard>

        <SectionCard title="About page content">
          <Field label="Headline"><TextInput value={s.about.headline} onChange={(v) => set('about.headline', v)} /></Field>
          <ParagraphList label="Paragraphs" paragraphs={s.about.paragraphs} onChange={(v) => set('about.paragraphs', v)} />
        </SectionCard>

        <SectionCard title="Contact page text">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Eyebrow"><TextInput value={s.contact_page?.eyebrow || ''} onChange={(v) => set('contact_page.eyebrow', v)} /></Field>
            <Field label="Title"><TextInput value={s.contact_page?.title || ''} onChange={(v) => set('contact_page.title', v)} /></Field>
          </div>
          <Field label="Description"><TextArea rows={2} value={s.contact_page?.description || ''} onChange={(v) => set('contact_page.description', v)} /></Field>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Form title"><TextInput value={s.contact_page?.form_title || ''} onChange={(v) => set('contact_page.form_title', v)} /></Field>
            <Field label="Form subtitle"><TextInput value={s.contact_page?.form_subtitle || ''} onChange={(v) => set('contact_page.form_subtitle', v)} /></Field>
          </div>
          <Field label="Reply note"><TextInput value={s.contact_page?.reply_note || ''} onChange={(v) => set('contact_page.reply_note', v)} /></Field>
        </SectionCard>

        <SectionCard title="Contact information" description="Shown in the footer and on the Contact page.">
          <Field label="Address"><TextArea rows={2} value={s.contact.address} onChange={(v) => set('contact.address', v)} /></Field>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Email"><TextInput value={s.contact.email} onChange={(v) => set('contact.email', v)} /></Field>
            <Field label="Phone"><TextInput value={s.contact.phone} onChange={(v) => set('contact.phone', v)} /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Social media" description="Leave any field empty to hide that icon.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Facebook URL"><TextInput value={s.social.facebook} onChange={(v) => set('social.facebook', v)} placeholder="https://facebook.com/..." /></Field>
            <Field label="Twitter / X URL"><TextInput value={s.social.twitter} onChange={(v) => set('social.twitter', v)} placeholder="https://twitter.com/..." /></Field>
            <Field label="YouTube URL"><TextInput value={s.social.youtube} onChange={(v) => set('social.youtube', v)} placeholder="https://youtube.com/..." /></Field>
            <Field label="Instagram URL"><TextInput value={s.social.instagram} onChange={(v) => set('social.instagram', v)} placeholder="https://instagram.com/..." /></Field>
            <Field label="LinkedIn URL"><TextInput value={s.social.linkedin} onChange={(v) => set('social.linkedin', v)} placeholder="https://linkedin.com/..." /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Footer settings">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field label="Government heading"><TextInput value={s.footer?.government_heading || ''} onChange={(v) => set('footer.government_heading', v)} /></Field>
            <Field label="Quick Links heading"><TextInput value={s.footer?.quick_links_heading || ''} onChange={(v) => set('footer.quick_links_heading', v)} /></Field>
            <Field label="Contact heading"><TextInput value={s.footer?.contact_heading || ''} onChange={(v) => set('footer.contact_heading', v)} /></Field>
          </div>
          <Field label="Copyright text" hint="Use {year} and {site_name} placeholders">
            <TextInput value={s.footer?.copyright_template || ''} onChange={(v) => set('footer.copyright_template', v)} />
          </Field>
          <LegalLinksEditor
            links={s.footer?.legal_links}
            onChange={(v) => set('footer.legal_links', v)}
          />
        </SectionCard>
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <Button onClick={save} disabled={saving} className="shadow-xl">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save all settings
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
